import React from 'react'
import { Button, ButtonProps, ButtonShape, ButtonStyle } from './button'
import { IconCaretDown, IconCaretRight, IconCheckSquare, IconSquare } from '../../icons/library'
import { IconOnlyCheckbox } from '../form/checkbox'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'

interface ListButtonProps extends ButtonProps {
  /** The content to display on the button */
  children: React.ReactNode
  /** Callback function when the button is clicked */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  /** Optional sub-menu to toggle when the button is clicked */
  subMenu?: React.ReactNode
  /** Whether the button is highlighted or not */
  highlighted?: boolean
  /** Whether the button should have a light highlight */
  lightHighlight?: boolean
  /** Additional class names for styling */
  className?: string
  /** Shortcut to make the buttonShape thin */
  thin?: boolean
  /** Whether the button should be inset on the left */
  inset?: boolean
  /** The shape of the button. Likely does not need to be changed */
  shape?: ButtonShape
  /** The style of the button. Likely does not need to be changed */
  buttonStyle?: ButtonStyle
}

/**
 * A styled button that is used on the left side of the screen in three panel layout.
 */
export const ListButton: React.FC<ListButtonProps> = ({
  children,
  onClick,
  subMenu,
  highlighted,
  lightHighlight,
  className,
  thin,
  inset,
  shape = thin ? ButtonShape.listButtonThin : ButtonShape.listButton,
  buttonStyle = ButtonStyle.listButton,
  ...props
}) => {
  const [open, setOpen] = React.useState(false)

  return (
    <div className={`flex flex-col ${className}`}>
      <Button
        {...props}
        onClick={(e) => {
          setOpen(!open)
          if (onClick) {
            onClick(e)
          }
        }}
        buttonStyle={buttonStyle}
        shape={shape}
        className={`${lightHighlight ? 'bg-charcoal-active' : highlighted ? '!bg-charcoal-light' : ''} ${inset ? '!pl-4' : ''} select-none overflow-hidden !text-wrap text-start leading-5`}
      >
        {subMenu &&
          (open ? (
            <IconCaretDown className={`${inset ? '-ml-2' : ''} mr-2`} />
          ) : (
            <IconCaretRight className={`${inset ? '-ml-2' : ''} mr-2 pr-[1.75px]`} />
          ))}
        {children}
      </Button>
      {open && subMenu && (
        <div className="mx-3 mt-1 flex flex-col gap-0.5 self-stretch border-l border-charcoal-active py-1 pl-2">
          {subMenu}
        </div>
      )}
    </div>
  )
}

interface ListCheckboxProps {
  checked: boolean
  onChecked?: (checked: boolean) => void
  className?: string
  size?: SizeProp
}

export const ListButtonCheckbox: React.FC<ListCheckboxProps> = ({ checked, onChecked, className, size = 'lg' }) => {
  return (
    <IconOnlyCheckbox
      checked={checked}
      onChecked={onChecked}
      size={size}
      className={`ml-auto mr-1 pl-2 hover:!text-logo ${className}`}
    />
  )
}
